import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type ColumnsConfig<C extends string> = {
  /** Ordem de exibição; colunas sempre visíveis (ex.: "Ações") ficam de fora. */
  columns: readonly C[];
  defaultColumns: readonly C[];
  storageKey: string;
  /** Ao mudar as colunas, suba a versão. */
  version: number;
};

type ColumnsStore<C extends string> = {
  visibleColumns: C[];
  setVisibleColumns: (columns: C[]) => void;
};

export type ColumnsState<C extends string> = {
  columns: readonly C[];
  defaultColumns: readonly C[];
  visibleColumns: C[];
  isVisible: (column: C) => boolean;
  setVisibleColumns: (columns: C[]) => void;
};

/** Preferência de visualização, não filtro: `localStorage`, não URL. Um hook por tabela. */
export function createColumnsHook<C extends string>({
  columns,
  defaultColumns,
  storageKey,
  version,
}: ColumnsConfig<C>) {
  /** Descarta ids que não existem mais (lista vinda do `localStorage`). */
  const normalize = (list: readonly string[]): C[] =>
    columns.filter((column) => list.includes(column));

  const useStore = create<ColumnsStore<C>>()(
    persist(
      (set) => ({
        visibleColumns: [...defaultColumns],
        setVisibleColumns: (next) => set({ visibleColumns: normalize(next) }),
      }),
      {
        name: storageKey,
        version,
        partialize: (state) => ({ visibleColumns: state.visibleColumns }),
        merge: (persisted, current) => {
          const stored = (persisted as Partial<ColumnsStore<C>> | undefined)?.visibleColumns;
          const visibleColumns = Array.isArray(stored) ? normalize(stored) : [];
          return visibleColumns.length > 0 ? { ...current, visibleColumns } : current;
        },
      },
    ),
  );

  return function useColumns(): ColumnsState<C> {
    const visibleColumns = useStore((state) => state.visibleColumns);
    const setVisibleColumns = useStore((state) => state.setVisibleColumns);

    return {
      columns,
      defaultColumns,
      visibleColumns,
      isVisible: (column) => visibleColumns.includes(column),
      setVisibleColumns,
    };
  };
}
